import cx from 'classnames'
import * as React from 'react'

import Button from '../button/button'
import Icon from '../icon'
import { Props as IconProps } from '../icon/icon'
import TextButton from '../text-button'
import VisuallyHidden from '../visually-hidden'
import styles from './copy-to-clipboard-button.module.scss'
import { copyToClipboard, copyToClipboardIsSupported } from './utils'

export type CopyToClipboardProps = {
  name: string
  value: string
  size?: IconProps['size']
  className?: string
  customText?: string
  showCopyIcon?: boolean
  showTitle?: boolean
  onClick?: () => void
} & React.ComponentPropsWithoutRef<'button'>

export default function CopyToClipboardButton({
  name,
  value,
  size = 16,
  className = '',
  customText,
  title = 'Copy to clipboard',
  showTitle = true,
  onClick,
  showCopyIcon = true,
  ...otherProps
}: CopyToClipboardProps) {
  const timeoutRef = React.useRef<number | null>(null)
  const [hasCopied, setHasCopied] = React.useState<boolean>(false)
  const hiddenTextPrefix = hasCopied ? 'Copied' : 'Copy'
  const titleText = showTitle ? title : ''

  const handleClick = React.useCallback(async () => {
    await copyToClipboard(value)
    setHasCopied(true)

    if (timeoutRef.current != null) {
      window.clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = window.setTimeout(() => setHasCopied(false), 3000)

    if (onClick != null) {
      onClick()
    }
  }, [setHasCopied, value, onClick])

  React.useEffect(() => {
    // On unmount lets delete the timeout. So that we don't leave it hanging about in memory.
    return () => {
      if (timeoutRef.current != null) {
        window.clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  if (!copyToClipboardIsSupported()) {
    return null
  }

  if (customText) {
    return (
      <Button
        type="button"
        {...otherProps}
        className={className}
        title={titleText}
        onClick={handleClick}
      >
        <span className={styles.customText}>{customText}</span>

        {showCopyIcon && (
          <Icon size={size} icon={hasCopied ? 'circle-checkmark' : 'copy'} />
        )}

        <VisuallyHidden is="span" aria-live="assertive">
          {hiddenTextPrefix} {name} to clipboard
        </VisuallyHidden>
      </Button>
    )
  }

  return (
    <TextButton
      {...otherProps}
      className={cx(className, styles.button, {
        [styles.copied]: hasCopied,
      })}
      type="button"
      title={titleText}
      onClick={handleClick}
      marginLeft="extra-small"
    >
      <Icon size={size} icon={hasCopied ? 'circle-checkmark' : 'copy'} />

      <VisuallyHidden is="span" aria-live="assertive">
        {hiddenTextPrefix} {name} to clipboard
      </VisuallyHidden>
    </TextButton>
  )
}
